import { Text } from "@ui-kitten/components";
import BaseCard, { BaseCardListItem } from "components/BaseCard";
import BottomSheet from "components/BottomSheet/BottomSheet";
import InnerLoading from "components/InnerLoading";
import dayjs from "dayjs";
import {
  setIsChangingSeat,
  setOriginalEventId,
  setOriginalTickets,
  setSelectedEvent,
  setSelectedPartySize,
  setSelectedSubscription,
} from "features/slices/reservationFlowSlice";
import {
  EventAvailabilityDetails,
  EventDetails,
  GroupTicketDetails,
  SeatDetails,
  TicketDetails,
} from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import i18n from "plugins/localization";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import { useFetchExchangeEventAvailablitiesQuery } from "queries/ticket.queries";
import React, { useState } from "react";
import { View } from "react-native";
import ScheduleItemList from "screens/Schedule/ScheduleItemList";
import { toSeatSelection } from "utils/navigationUtils";
import GroupDialog from "./GroupDialog";

interface EventActionsProps {
  seat: SeatDetails;
  event: EventDetails;
  groupCode: string;
  releaseAllBtnPress: () => void;
  tickets: TicketDetails[];
  groupTickets: GroupTicketDetails[];
}

const isTicketReservedByPrimaryComponent = (ticket: GroupTicketDetails) => {
  return ["BANK", "RESERVATION", "VOUCHER"].includes(
    ticket.planPackageComponentType,
  );
};

const EventActions: React.FC<EventActionsProps> = (props) => {
  // props
  const { seat, event, groupCode, releaseAllBtnPress, groupTickets, tickets } =
    props;
  // custom hooks
  const { account } = useAccount();

  // local states
  const [groupDialog, setGroupDialog] = useState(false);
  const [bottomSheetVisible, setBottomSheetVisible] = useState(false);
  // find the plan package id that was used to reserve the current event
  const planPackageId = groupTickets.find(
    isTicketReservedByPrimaryComponent,
  )?.planPackageId;

  const { data: tenantConfiguration } = useFetchTenantConfigurationQuery();

  // find the subscription that was used to reserve the current event
  const subscription = account?.subscriptions
    .filter((sub) => !sub.expired)
    .filter((sub) => sub.planPackage.id === planPackageId)[0];

  // react queries — must be called before any early return to satisfy rules of hooks
  const {
    data: exchangeEventAvailablities,
    refetch: fetchExchangeEventAvailablities,
    isFetching: isExchangeEventAvailablitiesFetching,
  } = useFetchExchangeEventAvailablitiesQuery({
    planPackageId: planPackageId ?? 0,
    ticketIds: tickets.map((t) => t.id),
  });

  if (!planPackageId) {
    return null;
  }

  // redux
  const dispatch = useAppDispatch();

  // utilities variables
  const isStanardSeating = seat.premiumCode === null;

  const isNotGroupSeats =
    groupTickets.filter((t) => t.status === "RESERVED").length ===
    tickets.length;

  const showCancelAllBtn =
    !subscription?.planPackage.eventCancelDisabled &&
    groupTickets
      .filter((groupTicket) => groupTicket.status !== "HOLD")
      .filter((groupTicket) => groupTicket.reservedByAccountId === account?.id)
      .every((ticket) => {
        if (
          // if account has guest pass that is claimed by someone
          ticket.planPackageComponentType?.includes("GUEST") &&
          ticket.accountId !== account?.id
        ) {
          return false;
        }
        if (ticket.accountId === account?.id && ticket.status === "RESERVED") {
          return true;
        }
        return false;
      });

  const handleChangeSeatsClick = async () => {
    const response = await fetchExchangeEventAvailablities();
    if (response.error) {
      return;
    }
    const eventAvailablities = response.data;
    const evt = eventAvailablities?.find((evt) => evt.eventId === event.id);
    if (evt) {
      dispatch(setSelectedSubscription(subscription!));
      dispatch(setIsChangingSeat(true));
      dispatch(setSelectedEvent(evt));
      dispatch(setOriginalTickets(tickets));
      dispatch(setSelectedPartySize(0));
      toSeatSelection();
    }
  };

  const handleSwitchEventClick = async () => {
    setBottomSheetVisible(true);
    fetchExchangeEventAvailablities();
  };

  const onScheduleItemClick = (schedule: EventAvailabilityDetails) => {
    dispatch(setSelectedSubscription(subscription!));
    dispatch(setIsChangingSeat(true));
    dispatch(setOriginalTickets(tickets));
    dispatch(setOriginalEventId(event.id));
    dispatch(setSelectedEvent(schedule));
    dispatch(setSelectedPartySize(0));
    toSeatSelection();
  };

  const ticketProvider = tickets[0].provider;

  const ticketUpgradable = () => {
    if (event.upgradeExchangeButtonHidden) return false;

    if (subscription === undefined) return false;

    // If plan requires approval and user cannot request themselves
    // This condition always takes precedence over other conditions
    const {
      planPackage: { ticketApprovalEnabled, userSelfRequestEnabled },
    } = subscription;

    if (ticketApprovalEnabled && !userSelfRequestEnabled) {
      return false;
    }

    if (isRSVP) return false;
    if (isStanardSeating) return true;
    if (isNotGroupSeats) return true;

    return true;
  };

  const minuteAfterReservation = dayjs()
    .utc()
    .diff(tickets[0].reservedAt, "minutes");

  if (ticketProvider === "AXS" && minuteAfterReservation < 30) {
    return (
      <Text category="h6">
        {`Your reservation can be modified after ${i18n.t("common.minute", {
          count: 30 - minuteAfterReservation,
        })}`}
      </Text>
    );
  }

  const isRSVP = event.type === "RSVP";

  return (
    <>
      <BaseCard title="Event Actions">
        {isStanardSeating && tenantConfiguration?.sitWithFriendsEnabled && (
          <BaseCardListItem
            title="Sit with friends"
            onPress={() => setGroupDialog(true)}
          ></BaseCardListItem>
        )}
        {ticketUpgradable() && (
          <>
            <BaseCardListItem
              title="Upgrade/Change Quantity"
              onPress={handleChangeSeatsClick}
            ></BaseCardListItem>
            <BaseCardListItem
              title="Exchange Event"
              onPress={handleSwitchEventClick}
            ></BaseCardListItem>
          </>
        )}
        {showCancelAllBtn && (
          <BaseCardListItem
            title="Cancel Booking"
            onPress={releaseAllBtnPress}
            testID="release-all-button"
          ></BaseCardListItem>
        )}
      </BaseCard>
      <GroupDialog
        visible={groupDialog}
        setVisible={setGroupDialog}
        seat={seat}
        event={event}
        groupCode={groupCode}
        onCancel={() => setGroupDialog(false)}
      />
      {!isRSVP && (
        <BottomSheet
          visible={bottomSheetVisible}
          onClose={() => setBottomSheetVisible(false)}
          title="Exchangeable Events"
        >
          {isExchangeEventAvailablitiesFetching ? (
            <View className="h-96">
              <InnerLoading text="" />
            </View>
          ) : (
            <>
              {exchangeEventAvailablities &&
              exchangeEventAvailablities.length > 0 ? (
                <View className="p-4">
                  <ScheduleItemList
                    schedule={exchangeEventAvailablities}
                    selectedSubscription={subscription!}
                    planPackageOperable={true}
                    onScheduleItemClick={onScheduleItemClick}
                  />
                </View>
              ) : (
                <Text category="h6">No exchangeable events</Text>
              )}
            </>
          )}
        </BottomSheet>
      )}
    </>
  );
};

export default EventActions;
